/**
 * NextBuySection: section 2 of the Screener page -- the ladder at cost, the next-buy suggestion and the own-tenor
 * shadow warning. Moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Display only.
 */
import React from "react";
import { SectionCard } from "@components/cockpit";
import LadderPriorityPanel from "@components/screener/LadderPriorityPanel";
import { mutedText, ui } from "@components/screener/screenerUi";
import { type ScreenerResponse } from "@lib/screenerTypes";
import { type TenorShadowRead } from "@lib/screenerTruth";

export type NextBuySectionProps = {
  data: ScreenerResponse;
  shadowRead: TenorShadowRead;
  strikeCounts: Record<string, number>;
};

export default function NextBuySection({
  data,
  shadowRead,
  strikeCounts,
}: NextBuySectionProps) {
  return (
    <section data-screener-section="next-buy" data-testid="screener-next-buy-section" className="mb-4">
      <SectionCard
        title="Next buy + the ladder at cost"
        subtitle="Where the held ladder has room, and the contract the ladder suggests next. A suggestion only: you choose what and how much."
      >
        {data.ladderPriority ? (
          <LadderPriorityPanel
            ladder={data.ladderPriority}
            textPrimary={ui.textPrimary}
            textMuted={ui.textMuted}
            cardBorder={ui.cardBorder}
            strikesByExpiry={strikeCounts}
          />
        ) : (
          <div className="text-xs" style={mutedText} data-testid="screener-ladder-not-published">
            This run did not publish the ladder view (older backend). Priority is tenor first, then Entry Score.
          </div>
        )}
        {/* TENOR VALUATION SHADOW — observation only, never affects ranking or sizing. Every candidate beyond
            913 DTE is valued at the SAME 3y node, so the long end can read "cheap" here while its own tenor
            says "rich". Phase 2: promoted from Surface Diagnostics to sit beside the next buy it qualifies. */}
        {shadowRead.show ? (
          <div className="mt-3">
                <div
                  className="rounded-lg px-2 py-2"
                  data-testid="screener-tenor-shadow"
                  data-warn={shadowRead.warn ? "true" : "false"}
                  style={
                    shadowRead.warn
                      ? { border: "1px solid rgba(245, 158, 11, 0.45)", backgroundColor: "rgba(245, 158, 11, 0.10)" }
                      : { border: `1px solid ${ui.cardBorder}` }
                  }
                  title={shadowRead.tooltip}
                >
                  <div className="text-xs font-semibold" style={shadowRead.warn ? { color: "rgb(var(--c-warn))" } : mutedText}>
                    {shadowRead.warn ? "⚠ Own-tenor check (shadow)" : "Own-tenor check (shadow)"}
                  </div>
                  <div className="mt-1 text-xs" style={{ color: ui.textPrimary }}>
                    {shadowRead.headline}.
                  </div>
                  {shadowRead.examples.length > 1 ? (
                    <div className="mt-1 text-[11px]" style={mutedText}>
                      Also: {shadowRead.examples.slice(1).map((e) => e.text).join(" · ")}
                    </div>
                  ) : null}
                  <div className="mt-1 text-[11px]" style={mutedText}>
                    Shadow only: the ranking, Priority order and sizes use the 3-year-proxy read.
                  </div>
                </div>
          </div>
        ) : null}
      </SectionCard>
    </section>
  );
}
